import { useTheme } from "@material-ui/core";
import React, { useEffect, useMemo, useRef } from "react";

import { AreaRenderer } from "../renderers/AreaRenderer";
import { GridRenderer } from "../renderers/GridRenderer";
import { PortRenderer } from "../renderers/PortRenderer";
import { RowRenderer } from "../renderers/RowRenderer";
import type { AreaBoundary, PortBoundary, RowBoundary, ViewState } from "../types/warehouse.types";
import { drawCircle } from "../utils/canvasDrawing";
import { clearCanvas } from "../utils/canvasUtils";
import { highlightSelectedRow } from "../utils/canvasHighlighting";

interface EditorCanvasRendererProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  canvasSize: { width: number; height: number };
  areaBoundaries: AreaBoundary[];
  rowBoundaries: RowBoundary[];
  portBoundaries: PortBoundary[];
  viewState: ViewState;
  worldToScreen: (x: number, y: number) => { x: number; y: number };
  showAreasPoints: boolean;
  showRowsPoints: boolean;
  showPortsPoints: boolean;
  selectedRowIndex?: number | null;
}

const POINT_RADIUS = 8;
const POINT_BORDER_WIDTH = 2;

export const EditorCanvasRenderer: React.FC<EditorCanvasRendererProps> = ({
  canvasRef,
  canvasSize,
  areaBoundaries,
  rowBoundaries,
  portBoundaries,
  viewState,
  worldToScreen,
  showAreasPoints,
  showRowsPoints,
  showPortsPoints,
  selectedRowIndex = null,
}) => {
  const theme = useTheme();

  // Initialize renderers (singleton instances)
  const gridRenderer = useRef(new GridRenderer(theme)).current;

  // Create renderers without labels
  const areaRenderer = useMemo(
    () =>
      new AreaRenderer(theme, {
        showCenterLabel: false,
      }),
    [theme]
  );

  const rowRenderer = useMemo(
    () =>
      new RowRenderer(theme, {
        showLabel: false,
      }),
    [theme]
  );

  const portRenderer = useMemo(
    () =>
      new PortRenderer(theme, {
        showLabel: false,
      }),
    [theme]
  );

  // Helper function to draw editable points on polygon
  const drawEditablePoints = (
    ctx: CanvasRenderingContext2D,
    polygon: Array<{ x: number; y: number }>,
    color: string
  ) => {
    polygon.forEach((point) => {
      const screenPoint = worldToScreen(point.x, point.y);
      drawCircle(ctx, screenPoint, POINT_RADIUS, {
        fill: true,
        stroke: true,
        style: {
          fillStyle: color,
          strokeStyle: "#000000",
          lineWidth: POINT_BORDER_WIDTH,
        },
      });
    });
  };

  // Main rendering effect
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    // Clear canvas and draw grid
    clearCanvas(ctx, canvasSize.width, canvasSize.height);
    gridRenderer.render(ctx, canvasSize.width, canvasSize.height, viewState);

    // Render port boundaries
    portRenderer.render(ctx, portBoundaries, viewState, worldToScreen);
    if (showPortsPoints) {
      // Draw editable points on ports
      portBoundaries.forEach((boundary) => {
        drawEditablePoints(ctx, boundary.polygon, boundary.borderColor);
      });
    }

    // Render area boundaries
    areaRenderer.render(ctx, areaBoundaries, viewState, worldToScreen, null);
    if (showAreasPoints) {
      // Draw editable points on areas
      areaBoundaries.forEach((boundary) => {
        drawEditablePoints(ctx, boundary.polygon, boundary.color);
      });
    }

    // Render row boundaries
    rowRenderer.render(
      ctx,
      rowBoundaries,
      viewState,
      worldToScreen,
      null,
      new Map(),
      areaBoundaries,
      new Set()
    );

    // Highlight selected row
    highlightSelectedRow(
      ctx,
      selectedRowIndex,
      rowBoundaries,
      worldToScreen,
      theme
    );

    if (showRowsPoints) {
      // Draw editable points on rows
      rowBoundaries.forEach((boundary) => {
        drawEditablePoints(ctx, boundary.polygon, boundary.color);
      });
    }
  }, [
    canvasRef,
    canvasSize,
    areaBoundaries,
    rowBoundaries,
    portBoundaries,
    viewState,
    worldToScreen,
    gridRenderer,
    areaRenderer,
    rowRenderer,
    portRenderer,
    showAreasPoints,
    showRowsPoints,
    showPortsPoints,
    selectedRowIndex,
    theme,
  ]);

  return null; // This is a rendering-only component
};
